import { useAuthState } from '@auth/hooks';
import { HeroBackground, SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Container, Section, ButtonType, Badge, BadgeSize, BadgeVariant } from '@soteria/design-system-ve';
import { CommunityAuthRequired } from '@soteria-ui/src/communities/community/components';
import { SortableDataList, type SortableTableColumn, type SortOption } from '@system-config/label-management/components/SortableDataList';
import { StandardPolicyDialog } from '@system-config/policy-management/components';
import type { StandardPolicy } from '@system-config/policy-management/domain';
import { useDeleteStandardPolicy, useStandardPolicies } from '@system-config/policy-management/hooks';
import { useState } from 'react';

import * as styles from './PolicyManagementPage.css';

function PolicyManagementPageComponent() {
  // Check if user is system admin - only system admins can access policy management
  const { isAuthenticated, isUserSystemAdmin } = useAuthState();

  const { policies, loading, errors } = useStandardPolicies();
  const { deleteStandardPolicy, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteStandardPolicy();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<StandardPolicy | undefined>(undefined);

  if (!isAuthenticated) {
    return <CommunityAuthRequired />;
  }

  // If user is not a system admin, don't render anything
  if (!isUserSystemAdmin) {
    return null;
  }

  const handleOpenDialog = (policy?: StandardPolicy) => {
    setSelectedPolicy(policy || undefined);
    setIsDialogOpen(true);
  };

  // Create the delete item context
  const deleteItemContext = {
    handler: async (policy: StandardPolicy) => await deleteStandardPolicy(policy.id),
    isDeleting,
    errors: deleteErrors,
    resetErrors,
  };

  const columns: SortableTableColumn<StandardPolicy>[] = [
    {
      key: 'name',
      header: 'Name',
      render: item => item.name,
      width: '25%',
      sortable: true,
      sortKey: 'name',
    },
    {
      key: 'description',
      header: 'Description',
      render: item => item.description || '-',
      width: '30%',
      sortable: true,
      sortKey: 'description',
    },
    {
      key: 'isEnabled',
      header: 'Status',
      render: item => {
        const variant = item.isEnabled ? BadgeVariant.Approved : BadgeVariant.Pending;
        const text = item.isEnabled ? 'ENABLED' : 'DISABLED';

        return <Badge size={BadgeSize.Medium} text={text} variant={variant} />;
      },
      width: '12%',
      sortable: true,
      sortKey: 'isEnabled',
    },
    {
      key: 'labels',
      header: 'Labels Count',
      render: item => item.labels.length.toString(),
      width: '12%',
      sortable: true,
      sortKey: 'labels',
    },
    {
      key: 'scope',
      header: 'Scope',
      render: item => <Badge size={BadgeSize.Medium} text={item.scope.toUpperCase()} />,
      width: '10%',
      sortable: true,
      sortKey: 'scope',
    },
    {
      key: 'isSystemManaged',
      header: 'System Managed',
      render: item => {
        const variant = item.isSystemManaged ? BadgeVariant.Approved : BadgeVariant.Default;
        const text = item.isSystemManaged ? 'YES' : 'NO';

        return <Badge size={BadgeSize.Small} text={text} variant={variant} />;
      },
      width: '11%',
      sortable: true,
      sortKey: 'isSystemManaged',
    },
  ];

  // Sort options for the filter bar
  const sortOptions: SortOption<StandardPolicy>[] = [
    { value: 'name', label: 'Name', sortKey: 'name' },
    { value: 'description', label: 'Description', sortKey: 'description' },
    { value: 'isEnabled', label: 'Status', sortKey: 'isEnabled' },
    { value: 'labels', label: 'Labels Count', sortKey: 'labels' },
    { value: 'scope', label: 'Scope', sortKey: 'scope' },
    { value: 'isSystemManaged', label: 'System Managed', sortKey: 'isSystemManaged' },
  ];

  return (
    <>
      <HeroBackground imageUrl="/hero/hero-4.png" />
      <Container className={styles.withHeroBackground} padding="xxlarge" size="xxl">
        <Section spacing="large">
          <SectionHeader
            action={{
              text: 'ADD POLICY',
              onClick: () => handleOpenDialog(undefined),
              buttonType: ButtonType.Primary,
            }}
            description="Manage standard policies across communities"
            title="Standard Policies"
          />

          {/* Standard Policies List */}
          <SortableDataList
            searchable
            showFilterBar
            columns={columns}
            data={policies}
            dataTypeName="standard policy"
            defaultSort={{ key: 'name', direction: 'asc' }}
            deleteItemContext={deleteItemContext}
            error={errors}
            isLoading={loading}
            searchKey="name"
            searchPlaceholder="Search policies by name..."
            sortOptions={sortOptions}
            onEdit={handleOpenDialog}
          />
        </Section>

        {/* Create/Edit Standard Policy Dialog */}
        <StandardPolicyDialog isOpen={isDialogOpen} policy={selectedPolicy} onClose={() => setIsDialogOpen(false)} />
      </Container>
    </>
  );
}

export const PolicyManagementPage = withAppInsightsTracking(PolicyManagementPageComponent, 'PolicyManagementPage');
